import React from 'react';
import { Page, Text, View } from '@react-pdf/renderer';
import pdf from '../tokens/pdfStyles';
import { colors, fontFamily, space, typeScale } from '../tokens/designTokens';
import { hasLayoutOverrides, resolveLayout, type PdfLayoutOverrides } from '../tokens/pdfLayout';
import styles from './ScreenPrimitives.module.scss';

export type TocEntry = {
    index: string;
    title: string;
    subtitle?: string;
    dataSource?: string;
    coverage?: string[];
};

export const PdfToc: React.FC<{ orgName: string; entries: TocEntry[]; layout?: PdfLayoutOverrides }> = ({ orgName, entries, layout }) => {
    // Mirror PdfPage: when overrides are absent use the static page style as-is
    // (byte-identical to the previous A4-only behaviour). When overrides exist,
    // resolve the effective paper + paddings so the TOC matches the body pages.
    const overriding = hasLayoutOverrides(layout);
    const L = overriding ? resolveLayout(layout) : null;
    const paperSize = L?.paperSize ?? 'A4';
    const pageStyle = L
        ? [pdf.page, { paddingTop: L.paddingTop, paddingBottom: L.paddingBottom, paddingLeft: L.paddingLeft, paddingRight: L.paddingRight }]
        : pdf.page;
    // When overrides exist the page's left/right inset and the running header/
    // footer's `left`/`right` (which come from the static `page` tokens) drift
    // apart unless we re-align them. Same pattern as PdfPage.
    const headerInset = L ? { left: L.paddingLeft, right: L.paddingRight, top: L.headerOffset } : null;
    const footerInset = L ? { left: L.paddingLeft, right: L.paddingRight, bottom: L.footerOffset } : null;
    // Proportional scaling of inline font sizes / widths / paddings so the
    // TOC visually matches whatever paper it lands on. A4 → 1.0 (no-op).
    // A3 → 1.414. Same approach as PdfCover. The imported pdfStyles tokens
    // (sectionTitle / sectionSubtitle / eyebrowText) are overridden inline
    // below where they need to grow.
    const A4_REF_WIDTH = 595.28;
    const scale = L ? L.pageWidth / A4_REF_WIDTH : 1;
    const s = (n: number) => n * scale;
    return (
        <Page size={paperSize} style={pageStyle} wrap>
            <View fixed style={headerInset ? [pdf.runningHeader, headerInset] : pdf.runningHeader}>
                <Text>{orgName}</Text>
                <Text>Contents</Text>
            </View>

            <View style={{ marginTop: s(space.sp4) }}>
                <View style={pdf.eyebrow} wrap={false}>
                    <Text style={[pdf.eyebrowText, { fontSize: s(typeScale.micro) }]}>Report Structure</Text>
                </View>
                <Text style={[pdf.sectionTitle, { fontSize: s(typeScale.h2) }]}>Contents</Text>
                <Text style={[pdf.sectionSubtitle, { fontSize: s(typeScale.bodySm) }]}>
                    {entries.length} module{entries.length === 1 ? '' : 's'} compiled from Recur Club's credit intelligence pipeline.
                </Text>
            </View>

            <View style={{ marginTop: s(space.sp6), borderTopWidth: 1.2, borderTopColor: colors.ink10 }}>
                {entries.map((e) => (
                    <View
                        key={e.index}
                        style={{
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            paddingVertical: s(space.sp3),
                            borderBottomWidth: 0.5,
                            borderBottomColor: colors.ruleSoft,
                            gap: s(space.sp3),
                        }}
                        wrap={false}
                    >
                        <Text
                            style={{
                                width: s(28),
                                fontSize: s(typeScale.metricSm),
                                fontFamily: fontFamily.mono,
                                color: colors.ink40,
                            }}
                        >
                            {e.index}
                        </Text>
                        <View style={{ flex: 1 }}>
                            <Text
                                style={{
                                    fontSize: s(10.5),
                                    fontFamily: fontFamily.body,
                                    fontWeight: 500,
                                    color: colors.ink10,
                                    letterSpacing: 0.6,
                                    textTransform: 'uppercase',
                                }}
                            >
                                {e.title}
                            </Text>
                            {e.subtitle ? (
                                <Text
                                    style={{
                                        fontSize: s(typeScale.caption),
                                        color: colors.ink40,
                                        marginTop: s(2),
                                        lineHeight: 1.4,
                                        textTransform: 'none',
                                        letterSpacing: 0,
                                        fontWeight: 400,
                                    }}
                                >
                                    {e.subtitle}
                                </Text>
                            ) : null}
                            {/* TODO: coverage chips suppressed during V2 reorder — re-enable once final TOC layout is locked.
                        {e.coverage && e.coverage.length ? (
                            <View
                                style={{
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    gap: 3,
                                    marginTop: 4,
                                }}
                            >
                                {e.coverage.map((c, i) => (
                                    <Text
                                        key={i}
                                        style={{
                                            fontSize: typeScale.micro,
                                            color: colors.ink40,
                                            backgroundColor: colors.surface,
                                            paddingHorizontal: 4,
                                            paddingVertical: 1,
                                            borderRadius: 2,
                                            letterSpacing: 0.3,
                                        }}
                                    >
                                        {c}
                                    </Text>
                                ))}
                            </View>
                        ) : null}
                        */}
                        </View>
                    </View>
                ))}
            </View>

            <View fixed style={footerInset ? [pdf.runningFooter, footerInset] : pdf.runningFooter}>
                <Text>AICA Report — Confidential</Text>
                <Text
                    render={({ pageNumber, totalPages }) =>
                        `${String(pageNumber).padStart(2, '0')} / ${String(totalPages).padStart(2, '0')}`
                    }
                />
            </View>
        </Page>
    );
};

export const ScreenToc: React.FC<{ orgName: string; entries: TocEntry[] }> = ({ orgName, entries }) => (
    <div className={styles.ScreenPage}>
        <div className={styles.RunningHeader}>
            <span>{orgName}</span>
            <span>Contents</span>
        </div>

        <div style={{ marginTop: 16 }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: '0.75rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: colors.ink40,
                    fontWeight: 600,
                }}
            >
                <span>Report Structure</span>
            </div>
            <div
                style={{
                    fontSize: '1.8rem',
                    fontFamily: "'Fraunces', Georgia, serif",
                    marginTop: 4,
                    lineHeight: 1.15,
                    color: colors.ink00,
                    letterSpacing: '-0.01em',
                }}
            >
                Contents
            </div>
            <div
                style={{
                    fontSize: '0.85rem',
                    color: colors.ink20,
                    marginTop: 6,
                    lineHeight: 1.5,
                    maxWidth: '85%',
                }}
            >
                {entries.length} module{entries.length === 1 ? '' : 's'} compiled from Recur Club's credit intelligence pipeline.
            </div>
        </div>

        <div style={{ marginTop: 20, borderTop: `1px solid ${colors.ink10}` }}>
            {entries.map((e) => (
                <div
                    key={e.index}
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        padding: '14px 0',
                        borderBottom: `1px solid ${colors.ruleSoft}`,
                        gap: 14,
                    }}
                >
                    <div
                        style={{
                            width: 36,
                            flexShrink: 0,
                            fontSize: '0.92rem',
                            fontFamily: "'Geist Mono', monospace",
                            color: colors.ink40,
                            paddingTop: 2,
                        }}
                    >
                        {e.index}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                            style={{
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                color: colors.ink00,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                            }}
                        >
                            {e.title}
                        </div>
                        {e.subtitle ? (
                            <div
                                style={{
                                    fontSize: '0.78rem',
                                    color: colors.ink40,
                                    marginTop: 4,
                                    lineHeight: 1.45,
                                }}
                            >
                                {e.subtitle}
                            </div>
                        ) : null}
                        {/* TODO: coverage chips suppressed during V2 reorder — re-enable once final TOC layout is locked.
                        {e.coverage && e.coverage.length ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                                {e.coverage.map((c, i) => (
                                    <span
                                        key={i}
                                        style={{
                                            fontSize: '0.66rem',
                                            color: colors.ink20,
                                            background: colors.surface,
                                            border: `0.5px solid ${colors.rule}`,
                                            padding: '2px 7px',
                                            borderRadius: 4,
                                            letterSpacing: '0.02em',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {c}
                                    </span>
                                ))}
                            </div>
                        ) : null}
                        */}
                    </div>
                </div>
            ))}
        </div>

        <div className={styles.RunningFooter}>
            <span>AICA Report &mdash; Confidential</span>
            <span>Contents</span>
        </div>
    </div>
);
